class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.syncRootState();
  }

  disconnectedCallback() {
    this.close();
  }

  syncRootState() {
    const hasOpenDrawer = Boolean(document.querySelector('cart-drawer.active'));
    document.documentElement.classList.toggle('cart-drawer-open', hasOpenDrawer);
    return hasOpenDrawer;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (!this.isConnected) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimeout);
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      if (!this.isConnected) return;
      this.classList.add('animate', 'active');
      this.syncRootState();
    });

    this.removeEventListener('transitionend', this.focusOnOpen);
    this.focusOnOpen = () => {
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement =
        this.querySelector('.drawer__inner') ||
        this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener(
      'transitionend',
      this.focusOnOpen,
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const wasActive = this.classList.contains('active');
    const hadPendingOpen = this.openTimeout != null;
    clearTimeout(this.openTimeout);
    clearTimeout(this.renderOpenTimeout);
    this.openTimeout = null;
    this.renderOpenTimeout = null;
    this.removeEventListener('transitionend', this.focusOnOpen);
    this.classList.remove('active');
    const hasOpenDrawer = this.syncRootState();
    if (wasActive) removeTrapFocus(this.activeElement);
    if (!hasOpenDrawer && (wasActive || hadPendingOpen)) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const sectionDocuments = new Map();
    const updates = this.getSectionsToRender().map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionDocuments.has(section.id)) {
        const html = parsedState.sections?.[section.id];
        if (typeof html !== 'string') {
          throw new Error(`Missing cart section: ${section.id}`);
        }
        sectionDocuments.set(
          section.id,
          new DOMParser().parseFromString(html, 'text/html')
        );
      }

      const source = sectionDocuments
        .get(section.id)
        .querySelector(section.selector || '.shopify-section');
      if (!sectionElement || !source) {
        throw new Error(
          `Missing cart fragment: ${section.selector || section.id}`
        );
      }

      // Stage every fragment before touching the current cart. Moving parsed
      // nodes avoids parsing the same markup again through innerHTML.
      const fragment = document.createDocumentFragment();
      fragment.append(...source.childNodes);
      return { sectionElement, fragment };
    });

    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    updates.forEach(({ sectionElement, fragment }) => {
      sectionElement.replaceChildren(fragment);
    });

    clearTimeout(this.renderOpenTimeout);
    this.renderOpenTimeout = setTimeout(() => {
      this.renderOpenTimeout = null;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-drawer',
        selector: '.drawer__empty',
      },
      {
        id: 'cart-drawer',
        selector: '.drawer__header',
      },
      {
        id: 'cart-drawer',
        selector: '.cart__footer-check-drawer-shipping',
      },
      {
        id: 'cart-drawer',
        selector: '.drawer__footer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    var mainCartItem = document.getElementById('main-cart-items');
    if (mainCartItem) {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '.js-contents',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: 'cart-drawer-items',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__empty',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__header',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.cart__footer-check-drawer-shipping',
        },
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__footer',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '.cart__footer-check-shipping',
        },
      ];
    }

    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__empty',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__header',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart__footer-check-drawer-shipping',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
